/**
 * @typedef DialogState
 * @property {boolean} render
 * @property {boolean} active
 */

/**
 * Mixin para controlar el estado de diálogos modales en los componentes.
 *
 * Muchos componentes utilizan diálogos que solo se utilizan ocasionalmente, pero que conllevan un coste de pintado
 * muy elevado. Por ello, es bueno no renderizar los componentes de los diálogos hasta que no son absolutamente
 * necesarios, utilizando un simple `v-if`.
 *
 * Este mixing provee funciones comunes para definir el estado de un diálogo, y poder abrirlo y cerrarlo de forma
 * sencilla. El estado de un diálogo se define con dos propiedades:
 *  - render: si el diálogo debe ser renderizado
 *  - active: si el diálogo se debe abrir.
 *
 * Al abrir un diálogo, el mixin se encarga de que el componente se pinte (render = true), esperar al siguiente tick de
 * Vue, y después hacer que el componente se active (active = true). Cerrar un diálogo hace el proceso a la inversa:
 * desactiva el componente, espera al siguiente tick de Vue y por último elimina el componente del marcado.
 * Así se respetan las animaciones de Vuetify.
 *
 * @example
 * export default {
 *   data: () => ({
 *     dialogState: {
 *       searcherModal: initDialog(),
 *     },
 *   }),
 * }
 * @example
 *   <nx-dialogo-costoso-de-renderizar
 *     v-if="dialogState.<dialogName>.render"
 *     :active="dialogState.<dialogName>.active"
 *     @dismiss="closeDialog(<dialogName>)"
 *   />
 * @see src/modules/bag/components/search/BagSearchCreateButton.vue
 */
const ManageDialogs = {
  /**
   * @return {{dialogState: Object<string, DialogState>}}}
   */
  data() {
    return { dialogState: {} };
  },
  methods: {
    async openDialog(dialog) {
      console.log('opening dialog', dialog);
      if (!this.dialogState[dialog]) {
        return;
      }

      this.dialogState[dialog].render = true;
      await this.$nextTick();

      this.dialogState[dialog].active = true;
    },
    async closeDialog(dialog) {
      if (!this.dialogState[dialog]) {
        return;
      }

      this.dialogState[dialog].active = false;
      await this.$nextTick();

      this.dialogState[dialog].render = false;
    },
  },
};

export default ManageDialogs;

/**
 * Inicializa el estado de un diálogo. Perfecto para ser usado directamente en la opción `data` de los componentes:
 *   data: () => ({
 *     dialogState: {
 *       buscador: initDialog(),
 *     }
 *   })
 *
 * @return {DialogState}
 */
export const initDialog = () => ({ render: false, active: false });
